import { useState, useEffect, useCallback, useContext } from 'react';
import { Trash2, RefreshCw, Mail, UserPlus, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { getInvites, createInvite, deleteInvite } from '@/services/user-invite.js';
import UserContext from '../../context/users';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogHeader, DialogContent, DialogFooter, ConfirmDialog } from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { cn } from '@/lib/cn';

function getInviteStatus(invite) {
    if (invite.status === 'accepted') return { label: 'Aceito', variant: 'success', icon: CheckCircle2 };
    const now = new Date();
    const expiresAt = new Date(invite.expiresAt);
    if (expiresAt < now) return { label: 'Expirado', variant: 'danger', icon: XCircle };
    return { label: 'Pendente', variant: 'info', icon: Clock };
}

function formatDate(dateStr) {
    return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function InvitesManager() {
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentUser] = useContext(UserContext);
    const [currentPage, setCurrentPage] = useState(1);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [inviting, setInviting] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const pageSize = 8;

    const loadInvites = useCallback(async () => {
        if (!currentUser?.companyID) return;
        setLoading(true);
        try {
            const { data } = await getInvites(currentUser.companyID);
            setInvites(data || []);
        } catch (e) {
            console.error(e);
            toast.error('Falha ao carregar convites');
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        loadInvites();
    }, [loadInvites]);

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await deleteInvite(deleteTarget.id);
            toast.success('Convite cancelado com sucesso');
            setDeleteTarget(null);
            loadInvites();
        } catch (e) {
            console.error(e);
            toast.error('Falha ao cancelar convite');
        } finally {
            setDeleting(false);
        }
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        if (!inviteEmail.trim()) return;
        setInviting(true);
        try {
            await createInvite(inviteEmail, currentUser.companyID);
            toast.success('Convite enviado com sucesso!');
            setInviteEmail('');
            setInviteOpen(false);
            loadInvites();
        } catch (err) {
            console.error(err);
            toast.error('Falha ao enviar convite');
        } finally {
            setInviting(false);
        }
    };

    const totalPages = Math.ceil(invites.length / pageSize);
    const paginatedInvites = invites.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    return (
        <>
            {/* Actions bar */}
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs" style={{ color: 'var(--dash-text-muted)' }}>
                    {invites.length} {invites.length === 1 ? 'convite' : 'convites'}
                </p>
                <div className="flex gap-2">
                    <Button variant="ghost" size="sm" leftIcon={RefreshCw} onClick={loadInvites}>
                        Atualizar
                    </Button>
                    <Button variant="primary" size="sm" leftIcon={UserPlus} onClick={() => setInviteOpen(true)}>
                        Convidar
                    </Button>
                </div>
            </div>

            {/* List */}
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex flex-col">
                            {[0, 1, 2, 3].map((i) => (
                                <div key={i} className="flex items-center gap-3 border-b px-4 py-3" style={{ borderColor: 'var(--dash-border)' }}>
                                    <div className="h-9 w-9 animate-pulse rounded-full" style={{ background: 'var(--dash-surface-hover)' }} />
                                    <div className="flex flex-1 flex-col gap-1.5">
                                        <div className="h-3.5 w-44 animate-pulse rounded-md" style={{ background: 'var(--dash-surface-hover)' }} />
                                        <div className="h-2.5 w-24 animate-pulse rounded-md" style={{ background: 'var(--dash-surface-hover)' }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : invites.length === 0 ? (
                        <EmptyState
                            icon={Mail}
                            title="Nenhum convite enviado"
                            description="Convide membros para a sua empresa por email."
                            action={
                                <Button variant="primary" size="sm" leftIcon={UserPlus} onClick={() => setInviteOpen(true)}>
                                    Convidar Usuário
                                </Button>
                            }
                        />
                    ) : (
                        <div className="flex flex-col">
                            {paginatedInvites.map((invite, index) => {
                                const status = getInviteStatus(invite);
                                return (
                                    <div
                                        key={invite.id}
                                        className={cn(
                                            'group flex items-center gap-3 px-4 py-3 transition-colors sm:px-5',
                                            index !== paginatedInvites.length - 1 && 'border-b'
                                        )}
                                        style={{ borderColor: 'var(--dash-border)' }}
                                        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--dash-surface-hover)')}
                                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                    >
                                        {/* Icon */}
                                        <div
                                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
                                            style={{
                                                background: 'var(--dash-primary-soft)',
                                                border: '1px solid var(--dash-primary-border)',
                                            }}
                                        >
                                            <Mail size={14} style={{ color: 'var(--dash-primary)' }} />
                                        </div>

                                        {/* Info */}
                                        <div className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-3">
                                            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                                                <span className="truncate text-sm font-medium" style={{ color: 'var(--dash-text)' }}>
                                                    {invite.email}
                                                </span>
                                                <span className="text-[11px]" style={{ color: 'var(--dash-text-faint)' }}>
                                                    Enviado em {formatDate(invite.createdAt)}
                                                </span>
                                            </div>
                                            <Badge variant={status.variant} icon={status.icon}>
                                                {status.label}
                                            </Badge>
                                        </div>

                                        {/* Delete */}
                                        {!invite.acceptedAt && (
                                            <button
                                                type="button"
                                                onClick={() => setDeleteTarget(invite)}
                                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg opacity-0 transition-all duration-200 group-hover:opacity-100"
                                                style={{ color: 'var(--dash-text-muted)', background: 'transparent' }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.color = '#d61f3f';
                                                    e.currentTarget.style.background = 'rgba(254,52,86,0.08)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.color = 'var(--dash-text-muted)';
                                                    e.currentTarget.style.background = 'transparent';
                                                }}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                            key={page}
                            type="button"
                            onClick={() => setCurrentPage(page)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-xs font-medium transition-all duration-200"
                            style={{
                                background: page === currentPage ? 'var(--dash-primary-soft)' : 'transparent',
                                color: page === currentPage ? 'var(--dash-primary)' : 'var(--dash-text-muted)',
                                border: page === currentPage ? '1px solid var(--dash-primary-border)' : '1px solid transparent',
                            }}
                        >
                            {page}
                        </button>
                    ))}
                </div>
            )}

            {/* Invite Dialog */}
            <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} size="sm">
                <DialogHeader
                    icon={UserPlus}
                    title="Convidar Usuário"
                    description="Envie um convite por email para adicionar um novo membro."
                />
                <form onSubmit={handleInvite}>
                    <DialogContent>
                        <Input
                            type="email"
                            leftIcon={Mail}
                            placeholder="email@exemplo.com"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            required
                        />
                    </DialogContent>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setInviteOpen(false)} disabled={inviting}>
                            Cancelar
                        </Button>
                        <Button type="submit" variant="primary" loading={inviting}>
                            Enviar Convite
                        </Button>
                    </DialogFooter>
                </form>
            </Dialog>

            {/* Delete Confirm */}
            <ConfirmDialog
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title="Cancelar convite"
                description={`Tem certeza que deseja cancelar o convite para ${deleteTarget?.email}?`}
                confirmLabel="Cancelar convite"
                loading={deleting}
            />
        </>
    );
}
